import React, { forwardRef } from 'react'
import * as UIcons from '@iconscout/react-unicons'
import ClickAwayComponent from 'components/ClickAwayComponent'
import { STATUS_TONE } from '../CampaignStatusTag'

const TAG_TONE = {
  success: { bg: 'bg-success-container', text: 'text-on-success-container', Icon: UIcons.UilCheckCircle },
  warning: { bg: 'bg-warning-container', text: 'text-gold', Icon: UIcons.UilClockThree },
  danger: { bg: 'bg-error-container', text: 'text-danger', Icon: UIcons.UilTimesCircle },
  neutral: { bg: 'bg-surface-container', text: 'text-on-surface-variant', Icon: UIcons.UilPause }
}

const FIELD_TEXT_TONE = { success: 'text-success', warning: 'text-gold', danger: 'text-danger', neutral: 'text-on-surface-variant' }

function StatusTag({ status }) {
  const toneKey = STATUS_TONE[(status ?? '').toLowerCase()] || 'neutral'
  const { bg, text, Icon } = TAG_TONE[toneKey]

  return (
    <span className={`inline-flex items-center gap-1 px-1 py-0.5 rounded whitespace-nowrap ${bg} ${text}`}>
      <Icon size={16} />
      <span className="text-xs">{(status ?? '').toUpperCase()}</span>
    </span>
  )
}

export default function CampaignDetailHeader({ campaign, onEdit, onDuplicate, onEndCampaign }) {
  const fields = [
    { label: 'Type', value: campaign.type },
    { label: 'Period', value: campaign.period.label, hint: `${campaign.period.daysLeft} days left` },
    { label: 'Budget', value: `NGN ${campaign.budget.toLocaleString()}.00`, hint: `${campaign.budgetUsedPercent}% used` },
    { label: 'Participants', value: campaign.participantsCount.toLocaleString() },
    { label: 'Performance', value: campaign.performanceMultiplier, icon: UIcons.UilArrowUp },
    { label: 'Status', value: campaign.status?.toUpperCase(), tone: STATUS_TONE[(campaign.status ?? '').toLowerCase()] || 'neutral' }
  ]

  return (
    <div className="w-full bg-white rounded-2xl border border-outline-variant shadow-[0px_1px_1px_0px_rgba(0,0,0,0.06)] p-6 mb-4">
      <div className="flex items-start gap-4 pb-4 border-b border-outline-variant">
        <div className="flex flex-1 gap-4 items-center min-w-0">
          <div className="w-[120px] h-20 rounded-lg bg-surface-container overflow-hidden shrink-0 flex items-center justify-center">
            {
              campaign.thumbnailUrl
                ? <img src={campaign.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                : <UIcons.UilGift size={28} className="text-on-surface-variant" />
            }
          </div>

          <div className="min-w-0 flex flex-col gap-2">
            <div className="flex items-center gap-4 flex-wrap">
              <p className="text-2xl font-semibold text-on-surface truncate">{campaign.title}</p>
              <StatusTag status={campaign.status} />
            </div>
            <p className="text-sm text-on-surface-variant truncate">{campaign.description}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            className="flex items-center gap-1.5 bg-surface-container rounded-full h-9 px-3.5 text-sm font-medium text-on-surface whitespace-nowrap"
          >
            Daily <UIcons.UilAngleDown size={14} className="text-on-surface-variant" />
          </button>

          <button
            type="button"
            onClick={onEdit}
            className="flex items-center gap-1.5 border border-outline-variant rounded-full h-9 px-3.5 text-sm font-medium text-on-surface whitespace-nowrap"
          >
            <UIcons.UilEditAlt size={15} /> Edit
          </button>

          <ClickAwayComponent
            Component={forwardRef(({ handleClick, open }, ref) => (
              <div className="relative" ref={ref}>
                <button
                  type="button"
                  onClick={handleClick}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-outline-variant"
                >
                  <UIcons.UilEllipsisV size={16} className="text-on-surface-variant" />
                </button>

                {
                  open && (
                    <div className="absolute right-0 top-11 z-20 w-48 bg-white border border-outline-variant rounded-xl shadow-lg py-2">
                      <button
                        type="button"
                        onClick={() => { onDuplicate(campaign); handleClick() }}
                        className="w-full flex items-center gap-2 text-left px-4 py-2 text-sm text-on-surface hover:bg-surface-container-low"
                      >
                        <UIcons.UilCopy size={16} /> Duplicate
                      </button>
                      <button
                        type="button"
                        onClick={() => { onEndCampaign(campaign); handleClick() }}
                        className="w-full flex items-center gap-2 text-left px-4 py-2 text-sm text-danger hover:bg-surface-container-low"
                      >
                        <UIcons.UilTimesSquare size={16} /> End Campaign
                      </button>
                    </div>
                  )
                }
              </div>
            ))}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-6 gap-4 pt-4">
        {
          fields.map((field) => (
            <div key={field.label}>
              <p className="text-xs font-semibold text-on-surface-variant uppercase tracking-wide">{field.label}</p>
              <div className="flex items-center gap-1 mt-1">
                {field.icon && <field.icon size={13} className="text-success" />}
                <p className={`text-base ${field.tone ? FIELD_TEXT_TONE[field.tone] : 'text-on-surface'}`}>{field.value}</p>
              </div>
              {field.hint && <p className="text-xs text-on-surface-variant mt-0.5">{field.hint}</p>}
            </div>
          ))
        }
      </div>
    </div>
  )
}
